import React, { useEffect, useState, useCallback } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { teamApi } from '../../services/api';

interface Team {
  id: number;
  name: string;
  long_name?: string;
  abbreviation?: string;
  is_international?: boolean;
  color?: string;
  created_at?: string;
  updated_at?: string;
}

const FootballTeams: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await teamApi.getAll();
      setTeams(res.data);
    } catch (err: any) {
      setError('Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleEdit = (id: number) => {
    navigate(`/football/teams/${id}/edit`);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await teamApi.delete(id);
      fetchTeams();
    } catch (err) {
      alert('Failed to delete team');
    }
  };

  const handleCreate = () => {
    navigate('/football/teams/new');
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
    { field: 'long_name', headerName: 'Long Name', flex: 1, minWidth: 180 },
    { field: 'abbreviation', headerName: 'Abbr.', width: 100 },
    { field: 'is_international', headerName: 'International', width: 120, type: 'boolean' },
    { field: 'color', headerName: 'Color', width: 100 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: { row: Team }) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => handleEdit(params.row.id)}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Football Teams</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Add Team
        </Button>
      </Stack>
      <DataGrid
        rows={teams}
        columns={columns}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        getRowId={(row) => row.id}
        sx={{ backgroundColor: 'background.paper' }}
      />
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
};

export default FootballTeams; 